<script lang="ts" setup>
  /**
   * MoreActions Component
   *
   * This component provides a responsive toolbar that automatically moves overflow items
   * into a "More" dropdown menu when space is limited.
   *
   * @example
   * <MoreActions>
   *   <Button data-action-id="edit">Edit</Button>
   *   <Button data-action-id="delete">Delete</Button>
   *   <Button data-action-id="share">Share</Button>
   *
   *   <template #more-actions>
   *     <DropdownItem data-action-id="edit">Edit Item</DropdownItem>
   *     <DropdownItem data-action-id="delete">Delete Item</DropdownItem>
   *     <DropdownItem data-action-id="share">Share Item</DropdownItem>
   *   </template>
   * </MoreActions>
   *
   * IMPORTANT:
   * - The #more-actions slot is REQUIRED. If not provided, the More dropdown will not be rendered.
   * - Each item in the default slot must have a corresponding item in the 'more-actions'
   *   slot with the same data-action-id attribute.
   * - The component automatically shows/hides matching items based on available space:
   *   - Items visible in toolbar → hidden in more-actions
   *   - Items hidden in toolbar (overflow) → visible in more-actions
   */
  import { uniqueId } from 'es-toolkit/compat';
  import { computed, onBeforeUnmount, ref, useSlots, useTemplateRef, watch } from 'vue';

  import { useToggleAnimation } from '../../composables/useToggleAnimation/useToggleAnimation';
  import { DEBOUNCE } from '../../constants';
  import { t } from '../../locale';
  import Button from '../Button/Button.vue';
  import Dropdown from '../Dropdown/Dropdown.vue';
  import Icon from '../Icon/Icon.vue';
  import { FADE_ANIMATION_DURATION, ID_PREFIXES, Z_INDEX } from './constants';
  import { createIntersectionObserver } from './createIntersectionObserver';
  import { useMoreButtonWidth } from './useMoreButtonWidth';
  import { useVisibleElementsWidth } from './useVisibleElementsWidth';

  export interface MoreActionsProps {
    /**
     * CSS class or classes to be applied to the actions container
     */
    actionsContainerClass?: string | Record<string, boolean> | undefined;
    /**
     * HTML element tag to use for the actions container
     * Useful when you need to maintain valid HTML semantics
     * (e.g., use 'ul' when children are <li> elements)
     * @default 'div'
     */
    actionsContainerTag?: string;
    /**
     * Text to display on the more actions button
     */
    moreButtonText?: string;
    /**
     * Optional width for the more-actions container
     * Applies style="width: {width}" to the stash-more-actions element
     */
    width?: string | number;
    /**
     * Rendering mode for dropdown items
     * - 'default': Button-style rendering (current behavior)
     * - 'custom': Custom rendering through slots
     */
    dropdownMode?: 'default' | 'custom';
    /**
     * Alignment of the More button
     * - 'separate': Button is aligned to the right edge of the actions container (default)
     * - 'together': Button is aligned immediately after the last visible action
     */
    moreButtonAlign?: 'separate' | 'together';
  }

  const props = withDefaults(defineProps<MoreActionsProps>(), {
    actionsContainerClass: undefined,
    actionsContainerTag: 'div',
    moreButtonText: () => t('ll.more'),
    dropdownMode: 'default',
    moreButtonAlign: 'separate',
    width: undefined,
  });

  // Check if more-actions slot is provided (REQUIRED for dropdown to render)
  const slots = useSlots();
  const hasMoreActionsSlot = computed(() => !!slots['more-actions']);

  // Refs for DOM elements
  const actionsContainerRef = useTemplateRef('actionsContainerRef');
  const moreDropdownRef = useTemplateRef('moreDropdownRef');
  const moreDropdownMenuRef = useTemplateRef('moreDropdownMenuRef');
  const trackerElementRef = useTemplateRef('trackerElementRef');

  // State management
  const overflowIds = ref<Set<string>>(new Set());
  const isMoreMenuOpen = ref(false);
  const moreMenuId = uniqueId(ID_PREFIXES.MORE_MENU);
  const showButtonDelayTimeout = ref<ReturnType<typeof setTimeout> | null>(null);
  const isDelayedShowReady = ref(false);

  const {
    isVisible: isMoreButtonVisible,
    shouldShow: shouldMoreButtonShow,
    toggle: toggleMoreButtonAnimation,
  } = useToggleAnimation({
    duration: FADE_ANIMATION_DURATION,
    initialVisible: false,
  });

  const hasOverflowActions = computed(() => overflowIds.value.size > 0);

  // Updated to use new Ref suffix naming convention
  const { calculateMoreButtonWidth, moreButtonWidth } = useMoreButtonWidth({
    moreDropdownRef,
    actionsContainerRef,
    moreButtonAlign: props.moreButtonAlign,
    hasOverflowActions,
  });

  // Updated to use new Ref suffix naming convention
  const {
    visibleElementsWidth,
    isRecalculatingWithDelay,
    calculateVisibleElementsWidthDelayed,
    cleanup: cleanupVisibleElementsWidth,
  } = useVisibleElementsWidth({
    actionsContainerRef,
    overflowIds,
  });

  const shouldShowMoreButton = computed(() => {
    return (
      hasMoreActionsSlot.value &&
      hasOverflowActions.value &&
      !isRecalculatingWithDelay.value &&
      isDelayedShowReady.value
    );
  });

  const isMoreMenuOpenString = computed(() => isMoreMenuOpen.value.toString());

  const actionsContainerWidth = computed(() => {
    if (!shouldShowMoreButton.value) {
      return '100%';
    }

    return `calc(100% - ${moreButtonWidth.value}px)`;
  });

  const moreButtonPosition = computed(() => {
    if (props.moreButtonAlign === 'together') {
      return { left: `${visibleElementsWidth.value}px`, right: 'auto' };
    } else {
      // Default separate alignment
      return { right: '0', left: 'auto' };
    }
  });

  createIntersectionObserver({
    actionsContainerRef,
    overflowIds,
    moreDropdownMenuRef,
    trackerElementRef,
    moreDropdownWidth: moreButtonWidth,
    calculateMoreButtonWidth,
    hasMoreActionsSlot,
    onOverflowChange: calculateVisibleElementsWidthDelayed,
  });

  // Watch for when recalculation completes and overflow actions exist
  watch(
    () => hasMoreActionsSlot.value && hasOverflowActions.value && !isRecalculatingWithDelay.value,
    (isReady) => {
      // Clear any existing timeout
      if (showButtonDelayTimeout.value) {
        clearTimeout(showButtonDelayTimeout.value);
        showButtonDelayTimeout.value = null;
      }

      if (isReady) {
        // Add delay before showing the button
        showButtonDelayTimeout.value = setTimeout(() => {
          isDelayedShowReady.value = true;
        }, DEBOUNCE.FAST);
      } else {
        // Hide immediately when overflow actions are removed or slot is not available
        isDelayedShowReady.value = false;
      }
    },
  );

  // Watch for changes in shouldShowMoreButton to handle fade animations
  watch(shouldShowMoreButton, (newValue) => {
    toggleMoreButtonAnimation(newValue);
  });

  function handleDropdownToggle(event: boolean) {
    isMoreMenuOpen.value = event;
  }

  onBeforeUnmount(() => {
    cleanupVisibleElementsWidth();
    if (showButtonDelayTimeout.value) {
      clearTimeout(showButtonDelayTimeout.value);
    }
  });
</script>

<template>
  <div
    class="stash-more-actions relative overflow-x-hidden"
    data-test="stash-more-actions"
    :style="{ width: props.width }"
  >
    <component
      :is="props.actionsContainerTag"
      ref="actionsContainerRef"
      data-test="stash-more-actions__container"
      :style="{
        width: actionsContainerWidth,
        minWidth: moreButtonWidth ? `${moreButtonWidth}px` : undefined,
      }"
      :class="['stash-more-actions__container flex items-center gap-2 overflow-x-hidden', props.actionsContainerClass]"
    >
      <!-- Default slot for elements that can be hidden -->
      <slot></slot>
    </component>

    <!-- Tracker Element - to detect when action elements overlap with it, triggering overflow behavior -->
    <div
      ref="trackerElementRef"
      class="stash-more-actions__tracker"
      :style="{
        position: 'absolute',
        right: '0px',
        top: '0',
        visibility: 'hidden',
        width: moreButtonWidth + 'px',
        height: '100%',
        pointerEvents: 'none',
        zIndex: Z_INDEX.TRACKER,
      }"
    ></div>

    <!-- Only render dropdown if more-actions slot is provided -->
    <Dropdown
      v-if="hasMoreActionsSlot"
      ref="moreDropdownRef"
      :class="[
        '!absolute top-0',
        shouldMoreButtonShow ? 'animate-fade-in' : 'animate-fade-out',
        { invisible: !isMoreButtonVisible },
      ]"
      :style="moreButtonPosition"
      @toggle="handleDropdownToggle"
    >
      <template #toggle="{ toggle }">
        <slot name="toggle" :toggle="toggle" :is-open="isMoreMenuOpen">
          <Button
            class="button -border-gray-500 text-gray-500 flex w-full items-center justify-between -border"
            secondary
            :aria-expanded="isMoreMenuOpenString"
            @click="toggle"
          >
            {{ props.moreButtonText }} <Icon class="-ml-1.5" name="action-dots" />
          </Button>
        </slot>
      </template>

      <template #default>
        <div
          :id="moreMenuId"
          ref="moreDropdownMenuRef"
          data-test="stash-more-actions__dropdown"
          class="flex flex-col gap-1.5 p-1.5"
        >
          <!-- More actions slot content (only overflow items will be shown) -->
          <slot name="more-actions"></slot>
        </div>
      </template>
    </Dropdown>
  </div>
</template>

<style module>
  :global([data-action-id]) {
    flex-shrink: 0;
  }

  :global(.stash-more-actions__container [data-action-id]:not([data-more-actions-processed])) {
    visibility: hidden;
  }
</style>
